<template>
  <MarketCard
    v-if="isSuccess(casinoData)"
    :class="[classes.imageContainer, 'TEST_CASINO']"
  >
    <LinkWrapper
      :class="classes.linkWrapper"
      target="_blank"
      role="button"
      :href="ctaLink"
      @click="onClick"
    >
      <div :class="[classes.content, 'col']">
        <div :class="[classes.mainContainer, 'col']">
          <img
            :class="[classes.img, layoutClass]"
            :src="casinoData.data.highlight.casino_game_image_url"
          />
        </div>
        <div :class="[classes.ctaContainer, 'row', layoutClass]">
          <FontFillText
            styleGroup="Casino Game Name"
            :minSize="8"
            :maxSize="16"
            :class="[classes.gameName, layoutClass]"
            :text="casinoData.data.highlight.casino_game_name"
            justifyContent="center"
            :alignment="layoutClass === 'large-mpu' ? 'center' : 'flex-start'"
            :getFont="text.get"
            :setFont="text.set"
          />

          <Button
            :class="[classes.ctaButton, layoutClass]"
            :optionName="casinoData.data.highlight.casino_game_name"
            :text="
              casinoData.data.highlight.casino_game_call_to_action || 'Play Now'
            "
          />
        </div>
      </div>
    </LinkWrapper>
  </MarketCard>
  <MarketCard
    v-else-if="isPending(casinoData) || isError(casinoData)"
    style="overflow: hidden;"
  >
    <Skeleton :class="classes.skeleton" />
    <Skeleton :class="classes.skeleton" />
  </MarketCard>
</template>

<script lang="ts">
import {
  isSuccess,
  isPending,
  isError,
  injectCasinoData
} from "../../../../lib/provideInjectors";
import MarketCard from "../../../styled/MarketCard.vue";
import Skeleton from "../Skeleton.vue";
import { computed, defineComponent, watchEffect } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import Button from "../CasinoButton.vue";

import FontFillText from "../../../FontFillText.vue";
import { Brand } from "@fresh8/types";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { injectCardNumber, useAnalytics } from "../../../../hooks/useAnalytics";

import * as generateLinks from "@fresh8/ad-data-composition/src/lib/generateLinks";
import { getCtaButton } from "../../../../hooks/useCtaButton";
import { injectBetData } from "@fresh8/ad-data-composition/src/betData";
import {
  injectTemplateSize,
  LARGE_MPU
} from "@fresh8/ad-data-composition/src/useTemplateSize";
import { camelize } from "@fresh8/ad-data-composition/src/lib/formatText";
import { injectPreview } from "@fresh8/ad-data-composition/src/preview";
import LinkWrapper from "../../../../components/LinkWrapper.vue";

const useStyles = createUseStyles({
  skeleton: {
    "&:first-of-type": {
      marginBottom: "2px",
      height: "100%",
      borderRadius: "var(--radius) var(--radius) 0 0"
    },
    "&:last-of-type": {
      marginTop: "0",
      height: "40px",
      borderRadius: " 0 0 var(--radius) var(--radius)"
    }
  },
  imageContainer: {
    "&.market-card": {
      position: "relative",
      overflow: "hidden",
      cursor: "pointer",
      background: "unset",
      backgroundImage: props => `url(${props.backgroundImage})`,
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover"
    },
    "& .row": {
      display: "flex",
      flexDirection: "row"
    },

    "& .col": {
      display: "flex",
      flexDirection: "column"
    }
  },

  img: {
    height: "90%",
    maxHeight: "120px",
    aspectRatio: "1/1",
    borderRadius: "20px",
    boxShadow: "0 2px 4px rgba(0,0,0,.2), 0 4px 6px rgba(0,0,0,.1)",
    backgroundColor: props => props.backgroundColor,

    [`&.${camelize(LARGE_MPU.name)}`]: {
      maxHeight: "207px"
    }
  },
  mainContainer: {
    // padding: "6px 0",
    flex: 1,
    justifyContent: "center",
    alignItems: "center"
  },
  content: {
    justifyContent: "space-between",
    height: "100%",
    display: "flex",
    flex: 1,
    backdropFilter: "blur(6px) saturate(0.7) brightness(0.8)"
  },
  ctaContainer: {
    boxSizing: "border-box",
    backgroundColor: "rgba(0,0,0,.4)",
    padding: "0 10px",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    height: "40px",

    [`&.${camelize(LARGE_MPU.name)}`]: {
      height: "104px",
      flexDirection: "column",

      padding: "12px"
    }
  },
  gameName: {
    boxSizing: "border-box",
    display: "flex",
    fontSize: "16px",
    lineHeight: "1",
    alignSelf: "center",
    maxWidth: "min(200px, 80%)",
    height: "30px !important",

    "& *": {
      verticalAlign: "middle"
    },

    [`&.${camelize(LARGE_MPU.name)}`]: {
      width: "100%",
      height: "unset !important",
      maxWidth: "unset",
      alignItems: "center"
    }
  },
  ctaButton: {
    //@ts-ignore
    boxSizing: "content-box !important",
    placeSelf: "center",

    padding: "2px 0 !important",
    width: "75px",

    "& span": {
      lineHeight: "1",
      textAlign: "center"
    },
    [`&.${camelize(LARGE_MPU.name)}`]: {
      padding: "8px 14px !important",
      width: "auto",
      height: "initial"
    }
  },
  linkWrapper: {
    height: "100%"
  }
});
//@ts-check
export default defineComponent({
  name: "CasinoHighlightMPU",
  components: {
    MarketCard,
    Skeleton,
    Button,
    FontFillText,
    LinkWrapper
  },
  setup() {
    const casinoData = injectCasinoData();
    const brandContext = injectBrand();
    const betData = injectBetData();
    const templateContext = injectTemplateSize();
    const previewContext = injectPreview()!;

    const cardNumber = injectCardNumber();

    const ctaButton = computed(() => getCtaButton());

    const href = computed(() =>
      generateLinks.generate(ctaButton.value.url, {
        affiliateId: betData?.affiliateId.value,
        clickUrl: betData?.clickUrl.value,
        operatorKey: betData?.operator.value?.name || ""
      })
    );

    const { handleClick: handleAnalytics } = useAnalytics(
      {
        cardNumber,
        optionName: isSuccess(casinoData.value)
          ? casinoData.value.data.highlight.casino_game_name
          : ""
      },
      "Casino Card"
    );

    const reactiveStyles = computed(() => {
      return {
        backgroundColor: brandContext?.getColorStyle(
          brandContext?.adTemplate.value?.backgroundColor
        ),
        backgroundImage:
          isSuccess(casinoData.value) &&
          casinoData.value.data.highlight.casino_game_image_url
      };
    });

    const classes = useStyles(reactiveStyles);

    function onClick() {
      /**
       * We have to have an onclick handler on marketcard div to make entire ad clickable.
       *  we cannot use a Anchor tag as that would make the button an "interactive element" inside an
       * Anchor tag which is invalid HTML (despite it rendering fine), percy will have a fit if you nest Achors
       * WE DO WHAT PERCY SAYS CAUSE APPRENTLY IT KNOWS BETTER
       **/

      if (href.value && !previewContext.preview.value.isPreview) {
        window.open(href.value, "_blank");
        handleAnalytics();
      }
    }

    const ctaLink = computed(() =>
      generateLinks.generate(ctaButton.value.url, {
        affiliateId: betData?.affiliateId.value,
        clickUrl: betData?.clickUrl.value,
        operatorKey: betData?.operator.value?.name || ""
      })
    );

    return {
      ctaLink,
      text: {
        get(brand: Brand | undefined) {
          if (brand) {
            return (
              brand.adTemplate.casino?.highlight.font ||
              brand.adTemplate.market?.casinoCTATextFont ||
              ""
            );
          }
        },
        set(brand: Brand, value: string) {
          if (brand.adTemplate.casino) {
            brand.adTemplate.casino.highlight.font = value;
          }
        }
      },
      layoutClass: computed(() => camelize(templateContext?.size.value.name)),
      onClick,
      classes,
      isError,
      isSuccess,
      isPending,
      casinoData
    };
  }
});
</script>
